import React, { memo } from 'react';
import { defaultTheme } from '@styles/theme.styled';
import { RichTextBlock } from 'prismic-reactjs';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import Section from '@components/section';
import { Wrapper } from '@components/wrapper.styled';
import {
  TitleContainer,
  MediaContainer,
  StyledLinkComponent,
  TextContent,
  FeatureWrapper,
} from './video.styled';

export interface VideoProps {
  video_url?: string;
  title?: string;
  subheading?: RichTextBlock[];
  description?: RichTextBlock[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  link?: {
    uid?: string;
    url?: string;
  };
  sectionStyle?: boolean;
  linkLabel?: string;
  'aria-label'?: string;
}

const Video: React.FC<VideoProps> = ({
  video_url,
  title,
  subheading,
  description,
  link,
  sectionStyle,
  linkLabel,
  backgroundColor = 'dark',
  'aria-label': ariaLabel,
}) => {
  const linkConfig = getLinkConfig(link);
  const hasVideo = Boolean(video_url);
  const hasSubheading = Boolean(subheading?.length);
  const hasDescription = Boolean(description?.length);
  const hasLink = Boolean(linkConfig.url);
  const hasTitleOrSubheading = Boolean(title || hasSubheading);

  return (
    <Section backgroundColor={sectionStyle ? 'white' : backgroundColor}>
      <Wrapper>
        <FeatureWrapper role="article" aria-label={ariaLabel || title} $sectionStyle={sectionStyle}>
          {hasVideo && (
            <MediaContainer>
              <iframe
                width="560"
                height="315"
                src={video_url}
                title={title || 'YouTube video'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                style={{ width: '100%', height: '100%', minHeight: 315, borderRadius: 12 }}
              />
            </MediaContainer>
          )}
          <TextContent>
            {hasTitleOrSubheading && (
              <TitleContainer>
                {title && (
                  <Text
                    tag="h2"
                    type="title"
                    stringText={title}
                    fontSize="heading2"
                    margin="none"
                    fontColor={backgroundColor === 'dark' ? 'white' : 'black'}
                  />
                )}
                {hasSubheading && (
                  <Text
                    type="subheading"
                    richText={subheading}
                    margin="xxs"
                    fontColor={backgroundColor === 'dark' ? 'primary' : 'darkPurple'}
                    fontSize="large"
                  />
                )}
              </TitleContainer>
            )}
            {hasDescription && (
              <Text
                type="description"
                richText={description}
                margin="xxs"
                fontColor={backgroundColor === 'dark' ? 'light' : 'dark'}
              />
            )}
            {hasLink && (
              <StyledLinkComponent
                displayArrow={false}
                type="button"
                isExternal={linkConfig.isExternal}
                url={linkConfig.url}
                title={linkLabel || 'Explore all products and features'}
                aria-label={linkLabel || 'Explore all products and features'}
              />
            )}
          </TextContent>
        </FeatureWrapper>
      </Wrapper>
    </Section>
  );
};

export default memo(Video);
